import { memo, useEffect, useRef, useState } from "react";

import { cn } from "utils";

interface DescribeProps {
  /** 描述文本内容 */
  desc: string;
  /** 最大显示行数，超过此行数将显示View more按钮，默认为8 */
  maxLines?: number;
  /** View more按钮文本，可以是字符串或ReactNode */
  viewMoreText?: React.ReactNode;
  /** 容器自定义类名 */
  className?: string;
  /** 内容区域自定义类名 */
  contentClassName?: string;
  /** View more按钮自定义类名 */
  viewMoreClassName?: string;
  /** 行数限制类名，默认为 line-clamp-8 */
  lineClampClassName?: string;
}

const Describe = ({
  desc,
  maxLines = 8,
  viewMoreText = "View more",
  className,
  contentClassName,
  viewMoreClassName,
  lineClampClassName = "line-clamp-8",
}: DescribeProps) => {
  const [expanded, setExpanded] = useState(false);
  const [showViewMore, setShowViewMore] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const toggleExpand = () => {
    setExpanded(true);
  };

  // 检测内容是否超过指定行数
  useEffect(() => {
    if (contentRef.current) {
      const lineHeight = parseInt(
        window.getComputedStyle(contentRef.current).lineHeight,
      );
      const height = contentRef.current.scrollHeight;
      const lines = Math.ceil(height / lineHeight);
      setShowViewMore(lines > maxLines);
    }
  }, [desc, maxLines]);

  if (!desc) {
    return null;
  }

  return (
    <div className={cn("relative", className)}>
      {/* 文本内容区域 */}
      <div
        ref={contentRef}
        className={cn(
          "body_m text-content_primary",
          !expanded && lineClampClassName,
          contentClassName,
        )}
        style={!expanded ? { WebkitLineClamp: maxLines } : undefined}
      >
        {desc}
      </div>

      {/* 只在内容超过指定行数且未展开时显示View more按钮 */}
      {!expanded && showViewMore && (
        <div className="mt-2 flex justify-start">
          <button
            aria-label="Describe"
            data-openreplay-label={`Describe Button | ${viewMoreText}`}
            id="btn-describe"
            onClick={toggleExpand}
            className={cn(
              "text-content_tertiary body_s cursor-pointer",
              viewMoreClassName,
            )}
          >
            {viewMoreText}
          </button>
        </div>
      )}
    </div>
  );
};

export default memo(Describe);
